import { Box, Skeleton, VStack } from '@chakra-ui/react';
import { EsrsReportStandardSwitch } from '../Report.types';
import { Typography } from 'Tokens';
import { BackToTopButton } from 'Atoms/BackToTopButton';
import { BottomStandardSwitcher } from '../ReportComponents/BottomStandardSwitcher';
import { RefObject, useMemo } from 'react';

type ReportContentContainerProps = {
  children: React.ReactNode;
  showBackToTop: boolean;
  standardIndex: number;
  setStandardIndex: (val: number) => void;
  standards?: EsrsReportStandardSwitch[];
  handleScroll: () => void;
  loading: boolean;
  reportTitle: string;
  reportRef: RefObject<HTMLDivElement | null>;
  selectedStandard?: EsrsReportStandardSwitch;
};

const ReportContentContainer = ({
  children,
  showBackToTop,
  standardIndex,
  setStandardIndex,
  standards,
  handleScroll,
  loading,
  reportTitle,
  reportRef,
  selectedStandard,
}: ReportContentContainerProps) => {
  const isFirstInCategory = useMemo(
    () =>
      standards
        ?.filter((std) => std.category === selectedStandard?.category)
        .findIndex((std) => std.title === selectedStandard?.title) === 0,
    [standards, selectedStandard]
  );

  return (
    <Box
      overflowY="scroll"
      height="100%"
      minW="780px"
      flex={1}
      maxWidth="1088px"
      pt="16px"
      onScroll={handleScroll}
      paddingInline="8px"
      css={{
        '&::-webkit-scrollbar': {
          width: '0',
        },
        scrollbarWidth: 'none',
      }}
      ref={reportRef}
    >
      <VStack
        minW="60%"
        maxWidth="1088px"
        p="72px 48px"
        shadow="interactive.accent"
        background="bg.elevated.interactive"
        alignItems="start"
        borderRadius="12px"
        gap="24px"
        minH=" 1200px"
      >
        {showBackToTop && <BackToTopButton containerRef={reportRef} />}
        {standardIndex === 0 && (
          <Skeleton isLoaded={!loading}>
            <Typography variant="d3" whiteSpace="normal">
              {reportTitle}
            </Typography>
          </Skeleton>
        )}
        {isFirstInCategory && <Typography variant="d3">{selectedStandard?.category}</Typography>}
        <Typography variant="h1">{selectedStandard?.title}</Typography>
        {children}
      </VStack>
      <BottomStandardSwitcher
        setStandardIndex={setStandardIndex}
        standards={standards}
        standardIndex={standardIndex}
      />
    </Box>
  );
};

export default ReportContentContainer;
